import type { ReactNode } from 'react';
import {
  contentStyle,
  flexClass,
  textClass,
  type CellType,
  type GridAlignment,
} from '@/shared/components/Grid';
import { DEFAULT_FORMATS, formatGridData, numberColor } from '@/shared/components/Grid/formats';
import { useI18n } from '@/shared/i18n/useT';
import { effectiveColor, type ThemeColorMode, type ThemeColors } from '@/shared/theme';

/**
 * With no `colores`/`modo` the preview simply resolves the live tokens, which
 * is what the alignment screen wants. The colours screen passes a palette and a
 * mode instead, so it can show the mode the viewer is *not* currently in.
 */
interface SheetPreviewProps {
  alineacion: GridAlignment;
  colores?: ThemeColors;
  modo?: ThemeColorMode;
}

const EDITABLES: CellType[] = ['text', 'number'];

export function SheetPreview({ alineacion, colores, modo }: SheetPreviewProps) {
  const { t } = useI18n();

  const CELL_TYPES: { type: CellType; label: string }[] = [
    { type: 'text', label: t('settings.grid.cellType.text') },
    { type: 'chip', label: t('settings.grid.cellType.chip') },
    { type: 'number', label: t('settings.grid.cellType.number') },
    { type: 'date', label: t('settings.grid.cellType.date') },
  ];

  const ROWS = [
    {
      text: t('settings.grid.sample.text'),
      chip: t('settings.grid.sample.chip'),
      number: 1284500.5,
      date: t('settings.grid.sample.date'),
    },
    {
      text: t('settings.grid.sample.shortText'),
      chip: 'En seguimiento',
      number: -39802.39,
      date: '01/08/2026',
    },
  ];

  // One accessor for the whole component: either the palette being edited, or
  // the live token when this preview is rendered without one.
  const rol = (base: string, fallback: string): string =>
    colores && modo ? effectiveColor(colores, modo, base) : fallback;

  const fondoCelda = (tipo: CellType, banda: 'clara' | 'oscura') => {
    const familia = EDITABLES.includes(tipo) ? 'edit' : 'ro';
    const keyBand = banda === 'clara' ? 'pale' : 'deep';
    const base = `cell_${familia === 'edit' ? 'edit' : 'readonly'}_${keyBand}`;
    return rol(base, `var(--cell-${familia}-${keyBand})`);
  };

  const estiloTabla =
    colores && modo
      ? { background: rol('background', ''), color: rol('text', '') }
      : undefined;
  const fondoFicha = rol(
    'chip',
    'color-mix(in srgb, var(--accent) 28%, var(--surface))',
  );

  const renderCell = (
    tipo: CellType,
    contenido: string,
    banda: 'clara' | 'oscura',
    colorText?: string,
  ): ReactNode => {
    const regla = alineacion[tipo];
    const c = contentStyle(regla);
    return (
      <td
        className={`border-b border-border p-2 align-top text-sm ${textClass(regla)} ${
          tipo === 'number' || tipo === 'date' ? 'tabular-nums' : ''
        }`}
        style={{ backgroundColor: fondoCelda(tipo, banda), ...(colorText ? { color: colorText } : null) }}
      >
        {tipo === 'chip' ? (
          <span
            className={`flex min-h-[24px] w-full items-center rounded-full border border-border py-1 pl-2.5 pr-6 text-xs font-bold ${flexClass(
              regla,
            )}`}
            style={{ backgroundColor: fondoFicha }}
          >
            <span className={`min-w-0 ${c.className}`} style={c.style}>
              {contenido}
            </span>
          </span>
        ) : (
          <div className={c.className} style={c.style}>
            {contenido}
          </div>
        )}
      </td>
    );
  };

  const editableLabels = CELL_TYPES.filter((ct) => EDITABLES.includes(ct.type))
    .map((ct) => ct.label)
    .join('”, “');

  const leyenda = colores ? (
    <p className="px-2 pb-2 pt-1 text-[0.6875rem] opacity-70">
      {t('settings.grid.previewLegend', { editable: editableLabels })}
    </p>
  ) : null;

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-surface" style={estiloTabla}>
      <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
        <thead
          className="border-b border-border text-muted"
          style={{
            backgroundColor: rol('header_fill', 'var(--grid-head)'),
            ...(colores && modo ? { color: rol('header_text', '') } : null),
          }}
        >
          <tr>
            {CELL_TYPES.map(({ type, label }) => (
              <th key={type} className={`p-2 text-xs font-bold ${textClass(alineacion.header)}`}>
                <div className={contentStyle(alineacion.header).className} style={contentStyle(alineacion.header).style}>{label}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ROWS.map((f, i) => {
            const banda = i % 2 === 0 ? 'clara' : 'oscura';
            const formattedNum = formatGridData(
              f.number,
              { type: 'number', formatKey: 'currency' },
              DEFAULT_FORMATS,
            );
            const colorNum = numberColor(f.number, formattedNum);
            return (
              <tr key={i}>
                {renderCell('text', f.text, banda)}
                {renderCell('chip', f.chip, banda)}
                {renderCell('number', formattedNum, banda, colorNum)}
                {renderCell('date', f.date, banda)}
              </tr>
            );
          })}
        </tbody>
      </table>
      {leyenda}
    </div>
  );
}
